import { CommonModule } from "@angular/common";
import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    Output,
    effect,
    inject,
    input
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { ContactService } from "@app/shared/services/contact.service";
import { DeviceService } from "@app/shared/services/device.service";
import { ReportNotesNavigationService } from "@app/shared/services/report-notes-navigation.service";
import { ScrollUtil } from "@arch/_shared/utils/scroll.util";
import { EventsService } from "@arch/domain/application/services/events.service";
import { ReportPreferenceService } from "@arch/domain/application/services/report-preference.service";
import {
    CompanyEntity,
    NoteEntity,
    ReportEntity,
    VisualizationEntity
} from "@arch/domain/entity";
import { UpdatePreferenceReportController } from "@arch/presentation/controllers";
import { AddNotesDialogComponent } from "./add-notes-dialog/add-notes-dialog.component";
import { CommentsPostDialogComponent } from "./comments-post-dialog/comments-post-dialog.component";
import { EditVisibleIndicatorsComponent } from "./edit-visible-indicators/edit-visible-indicators.component";

@Component({
    selector: "app-report-notes-navigation",
    imports: [CommonModule, SharedMaterialModule, SharedDsnModule, FormsModule],
    templateUrl: "./report-notes-navigation.component.html",
    styleUrl: "./report-notes-navigation.component.scss"
})
export class ReportNotesNavigationComponent implements AfterViewInit {
    @Input({ required: true })
    public visualizations: VisualizationEntity[];
    public company = input<CompanyEntity>(null);

    public report = input<ReportEntity>(null);
    @Input()
    public loading: boolean = true;
    @Output()
    public handleAccordion = new EventEmitter<string>();
    @Output()
    public handleChangeVisualizationsVisible = new EventEmitter();

    private readonly deviceService = inject(DeviceService);
    private readonly reportNotesNavigationService = inject(
        ReportNotesNavigationService
    );
    private readonly dialog = inject(MatDialog);
    private readonly contactService = inject(ContactService);
    public readonly preferenceService = inject(ReportPreferenceService);
    private readonly eventService = inject(EventsService);

    private readonly updateReportPrefecenteController = inject(
        UpdatePreferenceReportController
    );

    public contact = this.contactService.get();
    public reportIndex: {
        label: string;
        page: number;
        icon: string;
        active: boolean;
        show?: boolean;
        preference: string;
        description?: string;
        accordionId: string;
        tooltip?: string;
    }[] = [
        {
            label: "Benchmarking",
            description: "Mostrar no Dash",
            accordionId: "SectionBenchmarkig",
            preference: "viewBench",
            show: true,
            page: 1,
            icon: "fas fa-check",
            active: true
        },
        {
            label: "Performance",
            description: "Mostrar no Dash",
            accordionId: "SectionPerformance",
            preference: "viewPeformance",
            show: true,
            page: 3,
            icon: "fas fa-check",
            active: false
        },
        {
            label: "Chat Nanda IA",
            description: "Mostrar no Dash",
            accordionId: "",
            preference: "viewIA",
            show: true,
            page: 4,
            icon: "fas fa-check",
            active: false,
            tooltip:
                " Funcionalidade disponível apenas no Dash digital e no compartilhamento com contatos."
        }
    ];
    public activesAccordion: string = "SectionBenchmarking";
    public isScrolling: boolean = false;
    public isSyncingScroll: boolean = false;
    public isMobile = this.deviceService.getIsMobile();

    constructor() {
        effect(() => {
            const report = this.report();
            if (report) {
                this.preferenceService.setAll(report.props.optionsContent);
            }
        });
        effect(() => {
            const hasBenchmarkingData =
                this.reportNotesNavigationService.getHasBenchmarkingData()();

            this.handleBenchmarkingDataChange(hasBenchmarkingData);
        });
        effect(() => {
            const company = this.company();
            if (!company) return null;
            if (company.isGroup) this.removeBenchmarkingSection();
        });
    }

    ngAfterViewInit(): void {
        const trySyncScroll = (retries = 5) => {
            if (retries <= 0) return;
            const parentElement = document.getElementById(
                "ReportNotesNavigation"
            );
            const previewElement = document.getElementById(
                this.isMobile()
                    ? "ReportDashboardMobileScroll"
                    : "ReportPreviewScroll"
            );

            if (parentElement && previewElement) {
                this.onSyncScroll();
            } else if (retries > 0) {
                setTimeout(() => trySyncScroll(retries - 1), 300);
            }

            this.onSyncScroll();
        };
        trySyncScroll();
    }

    public onChangeSwitchPreference(preference: string) {
        this.preferenceService.set(
            preference,
            !this.preferenceService.get(preference)
        );
        const preferences = this.preferenceService.getAll();
        this.report().setOptionsContent(preferences);
        this.updateReportPrefecenteController.execute(this.report());
        this.eventService.send(
            `dash.preview.preferencia.${preference}.${!this.preferenceService.get(preference) ? "inativou" : "ativou"}`
        );
    }

    public async onSelectTopic(
        visualizationId: string,
        accordionId?: string
    ): Promise<void> {
        this.isSyncingScroll = true;
        if (
            visualizationId &&
            document.getElementById(`navigation-notes-${visualizationId}`)
        ) {
            await this.onSelectNavigation(visualizationId);
        }
        let hasOpended: boolean = false;
        let elementId = visualizationId
            ? `visualization-${visualizationId}`
            : accordionId;
        this.activeSecion(elementId);
        const el = document.getElementById(elementId);
        const previewElement = document.getElementById(
            this.isMobile()
                ? "ReportContentMobileScroll"
                : "ReportPreviewScroll"
        );
        const isScroll =
            previewElement.scrollHeight > previewElement.clientHeight;
        if (!isScroll) {
            hasOpended = true;
            this.handleAccordion.emit(accordionId);
            await this.sleep(500);
        }
        if (el && previewElement) {
            this.isScrolling = true;
            const isScolling = await ScrollUtil.scrollTo({
                elementParent: previewElement,
                elementChild: el
            });
            this.isScrolling = false;
            if (!isScolling) {
                this.handleAccordion.emit(accordionId);
            }
            if (hasOpended) {
                this.isSyncingScroll = false;
                return;
            }
            this.handleAccordion.emit(accordionId);
        }
        this.isSyncingScroll = false;
    }

    public async onSelectNavigation(navigationId: string): Promise<void> {
        const parentChild = document.getElementById("ReportNotesNavigation");
        const element = document.getElementById(
            `navigation-notes-${navigationId}`
        );
        this.isScrolling = true;
        await ScrollUtil.scrollTo({
            elementParent: parentChild,
            elementChild: element
        });
        this.isScrolling = false;
    }

    public onSyncScroll() {
        const parentElement = document.getElementById("ReportNotesNavigation");
        const previewElement = document.getElementById(
            this.isMobile()
                ? "ReportDashboardMobileScroll"
                : "ReportPreviewScroll"
        );

        if (!parentElement || !previewElement) return;

        let isSyncing = false;

        const syncScroll = (from: HTMLElement, to: HTMLElement) => {
            if (isSyncing || this.isScrolling || this.isSyncingScroll) return;
            isSyncing = true;

            const scrollRatio =
                from.scrollTop / (from.scrollHeight - from.clientHeight);
            to.scrollTop = scrollRatio * (to.scrollHeight - to.clientHeight);

            isSyncing = false;
        };

        parentElement.addEventListener("scroll", () =>
            syncScroll(parentElement, previewElement)
        );
        previewElement.addEventListener("scroll", () =>
            syncScroll(previewElement, parentElement)
        );
    }

    private activeSecion(sectionId: string) {
        this.activesAccordion = sectionId;
    }

    onOpenAddNoteDialog(visualization: VisualizationEntity): void {
        const dialogNote = this.dialog.open(AddNotesDialogComponent, {
            width: "100%",
            maxWidth: "700px",
            data: {
                report: this.report(),
                visualization: visualization
            }
        });

        dialogNote.afterClosed().subscribe(async (note: NoteEntity) => {
            if (!note) return;
            await this.sleep(100);
            const previewElement = document.getElementById(
                this.isMobile()
                    ? "ReportContentMobileScroll"
                    : "ReportPreviewScroll"
            );
            const childNoteElemeent = document.getElementById(
                `note-${note.props._id}`
            );
            this.isScrolling = true;
            await ScrollUtil.scrollTo({
                elementParent: previewElement,
                elementChild: childNoteElemeent
            });
            this.isScrolling = false;
        });
    }

    onOpenCommentsPostDialog(visualization: VisualizationEntity): void {
        this.dialog.open(CommentsPostDialogComponent, {
            width: "100%",
            maxWidth: "524px",
            height: "100%",
            maxHeight: "514px",
            data: {
                contact: this.contact,
                report: this.report(),
                visualization,
                company: this.company(),
                options: this.report().options
            }
        });
    }

    async sleep(time: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, time));
    }

    private handleBenchmarkingDataChange(hasBenchmarkingData: boolean): void {
        if (!hasBenchmarkingData) {
            this.removeBenchmarkingSection();
        }
    }

    private removeBenchmarkingSection(): void {
        this.reportIndex = this.reportIndex.filter(
            (item) => item.accordionId !== "SectionBenchmarkig"
        );
        const performanceIndex = this.reportIndex.find(
            (item) => item.accordionId === "SectionPerformance"
        );
        if (performanceIndex) {
            performanceIndex.active = true;
            this.activesAccordion = performanceIndex.accordionId;
        }
    }

    public onOpenEditIndicatorsDialog(): void {
        const dialog = this.dialog.open(EditVisibleIndicatorsComponent, {
            width: "524px",
            maxWidth: "524px",
            data: {
                report: this.report()
            }
        });
        dialog.afterClosed().subscribe((result) => {
            if (result) {
                this.report().setVisualizationsVisible(result);
                this.handleChangeVisualizationsVisible.emit();
            }
        });
    }
}
